import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import classnames from "classnames"
import Swal from "sweetalert2"
import {
  FetchPostData,
  fetchData,
  FetchDeleteData,
} from "utils/Common_api_functions"
import moment from "moment"
import { MdDelete, MdDeleteOutline } from "react-icons/md"

const MarketingEmailModal = ({ marketingEmail, setMarketingEmail }) => {
  const [activeTab, setActiveTab] = useState("send")
  const [formData, setFormData] = useState({
    subject: "",
    scheduledDate: "",
    content: "",
  })
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (marketingEmail) fetchEmails()
  }, [marketingEmail])

  const fetchEmails = async () => {
    const response = await fetchData(setLoading, "marketing/emails")
    if (response?.data) setEmails(response.data)
  }

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSend = async e => {
    e.preventDefault()
    const APIData = await FetchPostData(
      setLoading,
      "marketing/emails",
      formData
    )
    if (APIData?.data?.success) {
      Swal.fire({
        icon: "success",
        title: `Marketing Email ${
          formData?.scheduledDate ? "Scheduled" : "Sent"
        } Successfully`,
      })
      setFormData({ subject: "", scheduledDate: "", content: "" })
      fetchEmails()
      setActiveTab("list") // Switch to list after sending
    } else {
      Swal.fire({ icon: "error", title: "Something Went Wrong" })
    }
  }

  const handleDelete = async id => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this email?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    })
    if (confirm.isConfirmed) {
      const APIData = await FetchDeleteData(setLoading, `marketing/emails`, id)
      if (APIData?.data?.success) {
        Swal.fire("Deleted!", "Email has been deleted.", "success")
        fetchEmails()
      } else {
        Swal.fire("Something Went Wrong", "Email not Deleted", "error")
      }
    }
  }

  return (
    <Modal
      isOpen={marketingEmail}
      toggle={() => setMarketingEmail(false)}
      centered
      size="xl"
    >
      <ModalHeader toggle={() => setMarketingEmail(false)}>
        Marketing Emails
      </ModalHeader>
      <ModalBody>
        {/* Tabs */}
        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "send" })}
              onClick={() => toggleTab("send")}
              style={{ cursor: "pointer" }}
            >
              Send Email
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "list" })}
              onClick={() => toggleTab("list")}
              style={{ cursor: "pointer" }}
            >
              Email List
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="mt-3">
          {/* Send Email Tab */}
          <TabPane tabId="send">
            <Form onSubmit={handleSend}>
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label for="subject">
                      Subject <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="subject"
                      id="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="scheduledDate">Send Date</Label>
                    <Input
                      type="date"
                      name="scheduledDate"
                      id="scheduledDate"
                      min={new Date().toISOString().split("T")[0]}
                      value={formData.scheduledDate}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>
                  Content <span className="text-danger">*</span>
                </Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={formData.content}
                  onChange={(event, editor) => {
                    const data = editor.getData()
                    setFormData(prev => ({ ...prev, content: data }))
                  }}
                />
              </FormGroup>

              <div className="text-end mt-3">
                <Button color="primary" type="submit">
                  Send Email
                </Button>
              </div>
            </Form>
          </TabPane>

          {/* Email List Tab */}
          <TabPane tabId="list">
            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Schedule Date</th>
                  <th className="text-center">Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {emails?.length ? (
                  emails.map(email => (
                    <tr key={email._id}>
                      <td>{email.subject}</td>
                      <td>
                        {email.scheduledDate !== ""
                          ? moment(email.scheduledDate).format("DD-MM-YYYY")
                          : "Immediate"}
                      </td>
                      <td className="text-center">
                        <Badge
                          className="fs-6 px-3 py-2"
                          color={email.status ? "success" : "warning"}
                        >
                          {email.status ? "Delivered" : "Scheduled"}
                        </Badge>
                      </td>
                      <td>
                        {!email.sent && (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDelete(email._id)}
                          >
                            <MdDeleteOutline size={20} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No emails found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  )
}

export default MarketingEmailModal
